import { getErrorMessage } from '../utils/helpers'

import axios from './instance'

/**
 * TODO: This is currently unused. Remove?
 * Gets the list of courses a student is enrolled in, along with their progress.
 */
export const getStudentCourses = async ({ entityId }) => {
  try {
    return await axios.get(
      `/entities/${entityId}/course-records/?expand=course,student_certificate.certificate.training`,
    )
  } catch (err) {
    throw new Error(getErrorMessage(err))
  }
}

/**
 * Gets the details of a specific course for a student.
 * Does not include progress or student content.
 * @param {string} entityId - The ID of the entity.
 * @param {string} courseId - The ID of the course to retrieve.
 */
export const getStudentCourse = async (entityId, courseId) => {
  try {
    return await axios.get(
      `/entities/${entityId}/course-records/${courseId}/?expand=course,student_certificate.certificate.training,student_certificate.course_records.course,student_certificate.document`,
    )
  } catch (err) {
    throw new Error(getErrorMessage(err))
  }
}

/**
 * Gets the details of a specific course for a student, including content and progress.
 * Pass { includeQuizAttempts: true } to additionally batch-load quiz attempts inline
 * via ?expand=quiz_attempts — managers receive full attempt detail per record, which
 * lets instructor views avoid firing one getQuizAttempts request per quiz.
 * @param {string} entityId - The ID of the entity.
 * @param {string} courseRecordId - The ID of the course record to retrieve.
 * @param {Object} [options]
 * @param {boolean} [options.includeQuizAttempts=false]
 */
export const getStudentCourseContentRecords = async (
  entityId,
  courseRecordId,
  { includeQuizAttempts = false } = {},
) => {
  try {
    const expand = ['content', 'content.quiz']
    if (includeQuizAttempts) expand.push('quiz_attempts')
    const url =
      `/entities/${entityId}/course-content-records/` +
      `?course_record=${courseRecordId}&expand=${expand.join(',')}`
    return await axios.get(url)
  } catch (err) {
    throw new Error(getErrorMessage(err))
  }
}

/** * Gets a specific course content record for a student.
 * @param {string} entityId - The ID of the entity.
 * @param {string} courseContentRecordId - The ID of the course content record to retrieve.
 */
export const getStudentCourseContentRecord = async (entityId, courseContentRecordId) => {
  try {
    return await axios.get(`/entities/${entityId}/course-content-records/${courseContentRecordId}`)
  } catch (err) {
    throw new Error(getErrorMessage(err))
  }
}

/**
 * Starts a course for the current student
 * @param {string} entityId - The ID of the entity.
 * @param {string} courseId
 */
export const startCourse = async (entityId, courseId) => {
  try {
    return await axios.post(`/entities/${entityId}/course-records/${courseId}/start/`)
  } catch (err) {
    throw new Error(getErrorMessage(err))
  }
}
/**
 * Marks training complete for the given student certificate.
 * @param {string} entityId - The ID of the entity.
 * @param {string} studentCertificateId - The ID of the student certificate
 */
export const completeCourseTraining = async (entityId, studentCertificateId) => {
  try {
    return await axios.post(
      `/entities/${entityId}/student-certificates/${studentCertificateId}/complete_training/`,
    )
  } catch (err) {
    throw new Error(getErrorMessage(err))
  }
}

/**
 * Starts content within a course for the current student
 * @param {string} entityId - The ID of the entity.
 * @param {string} contentId
 */
export const startCourseContent = async (entityId, contentId) => {
  try {
    return await axios.post(`/entities/${entityId}/course-content-records/${contentId}/start/`)
  } catch (err) {
    throw new Error(getErrorMessage(err))
  }
}

/**
 * Marks a course content item as complete for the current student
 * @param {string} entityId - The ID of the entity.
 * @param {string} contentId
 */
export const completeCourseContent = async (entityId, contentId) => {
  try {
    return await axios.post(
      `/entities/${entityId}/course-content-records/${contentId}/track_view/`,
    )
  } catch (err) {
    throw new Error(getErrorMessage(err))
  }
}

/**
 * Mints a scoped, long-lived launch token for the Articulate iframe's xAPI calls.
 * The token replaces the learner's access token in the embed URL, so a long course
 * can't outlive the credential and a token refresh can't reload the player.
 * Returns `{ token }`.
 * @param {string} entityId - The ID of the entity.
 * @param {string} contentId - The ID of the course content record.
 */
export const getArticulateLaunchToken = async (entityId, contentId) => {
  try {
    return await axios.post(`/entities/${entityId}/course-content-records/${contentId}/launch/`)
  } catch (err) {
    throw new Error(getErrorMessage(err))
  }
}

/**
 * Submits responses for a quiz for the current student
 * @param {string} entityId - The ID of the entity.
 * @param {string} quizId
 * @param {Object} responses
 */
export const takeStudentQuiz = async (entityId, quizId, responses) => {
  try {
    return await axios.post(
      `/entities/${entityId}/course-content-records/${quizId}/take_quiz/`,
      JSON.stringify({ responses }),
    )
  } catch (err) {
    throw new Error(getErrorMessage(err))
  }
}

/**
 * Fetches all quiz attempts for a content record (instructor-only).
 * Returns an array of attempts. The endpoint currently returns a bare array,
 * but normalize against `results` in case it gets paginated like the rest of
 * our list endpoints — callers should always treat the return as an array.
 * @param {string} entityId - The ID of the entity.
 * @param {string} contentRecordId - The ID of the course content record.
 */
export const getQuizAttempts = async (entityId, contentRecordId) => {
  try {
    const response = await axios.get(
      `/entities/${entityId}/course-content-records/${contentRecordId}/quiz_attempts/`,
    )
    return response?.results ?? response
  } catch (err) {
    throw new Error(getErrorMessage(err))
  }
}

/**
 * Fetches the final attempt for a quiz
 * @param {string} entityId - The ID of the entity.
 * @param {string} quizId
 */
export const getQuizResult = async (entityId, quizId) => {
  try {
    return await axios.get(`/entities/${entityId}/course-content-records/${quizId}/quiz_result/`)
  } catch (err) {
    throw new Error(getErrorMessage(err))
  }
}
